import { Navigate, Route, Routes } from 'react-router-dom'
import PublicLayout from './components/site/PublicLayout'
import RequireRole from './components/shell/RequireRole'
import { schedulingServiceEnabled } from './lib/features'
import AboutPage from './pages/AboutPage'
import LoginPage from './pages/LoginPage'
import PrivacyPage from './pages/PrivacyPage'
import AccessibilityPage from './pages/AccessibilityPage'
import SecurityPage from './pages/SecurityPage'
import AgentDashboardPage from './pages/agent/AgentDashboardPage'
import AgentCalendarPage from './pages/agent/AgentCalendarPage'
import AgentCopySchedulePage from './pages/agent/AgentCopySchedulePage'
import AgentScheduleTemplatePage from './pages/agent/AgentScheduleTemplatePage'
import AgentAvailabilityPage from './pages/agent/AgentAvailabilityPage'
import AgentOpenShiftsPage from './pages/agent/AgentOpenShiftsPage'
import AgentSwapsPage from './pages/agent/AgentSwapsPage'
import NotificationSettingsPage from './pages/NotificationSettingsPage'
import AgentRequestLeavePage from './pages/agent/AgentRequestLeavePage'
import AgentRequestHistoryPage from './pages/agent/AgentRequestHistoryPage'
import SupervisorDashboardPage from './pages/supervisor/SupervisorDashboardPage'
import SupervisorApprovalInboxPage from './pages/supervisor/SupervisorApprovalInboxPage'
import SupervisorOpenShiftsPage from './pages/supervisor/SupervisorOpenShiftsPage'
import SupervisorPlanningBoardPage from './pages/supervisor/SupervisorPlanningBoardPage'
import SupervisorDailyAssignmentsPage from './pages/supervisor/SupervisorDailyAssignmentsPage'
import SupervisorPersonnelDirectoryPage from './pages/supervisor/SupervisorPersonnelDirectoryPage'
import SupervisorPersonnelProfilePage from './pages/supervisor/SupervisorPersonnelProfilePage'
import SupervisorStaffingTargetsPage from './pages/supervisor/SupervisorStaffingTargetsPage'
import AdminReportPage from './pages/reports/AdminReportPage'
import { REPORT_CATALOG } from './pages/reports/reportCatalog'
import SupervisorReportPage from './pages/reports/SupervisorReportPage'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import AdminAccountsPage from './pages/admin/AdminAccountsPage'
import AdminTeamsPage from './pages/admin/AdminTeamsPage'
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage'
import AdminShiftTemplatesPage from './pages/admin/AdminShiftTemplatesPage'
import AdminCodesPage from './pages/admin/AdminCodesPage'
import AdminLeaveTypesPage from './pages/admin/AdminLeaveTypesPage'
import LeadershipDashboardPage from './pages/leadership/LeadershipDashboardPage'

export default function App() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LoginPage />} />
        {/* /login redirects to the canonical URL rather than rendering a
            second copy, so a stale link still lands signed-in users where
            everyone else starts. */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/security" element={<SecurityPage />} />
        {/* Any other path, inside the public chrome rather than a blank
            page: nothing on this route needs a session. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* The agent and supervisor workspaces are their own surface: their
          own chrome, no utility banner or marketing footer. See
          docs/design/style-guide.md. Each is reachable only by a signed-in
          account with the matching role; see RequireRole. */}
      <Route
        path="/agent"
        element={
          <RequireRole role="agent">
            <AgentDashboardPage />
          </RequireRole>
        }
      />
      {schedulingServiceEnabled() && (
        <Route
          path="/account/notifications"
          element={
            <RequireRole role={['agent', 'supervisor', 'administrator', 'leadership']}>
              <NotificationSettingsPage />
            </RequireRole>
          }
        />
      )}
      {schedulingServiceEnabled() && (
        <Route
          path="/agent/swaps"
          element={
            <RequireRole role="agent">
              <AgentSwapsPage />
            </RequireRole>
          }
        />
      )}
      {schedulingServiceEnabled() && (
        <Route
          path="/agent/open-shifts"
          element={
            <RequireRole role="agent">
              <AgentOpenShiftsPage />
            </RequireRole>
          }
        />
      )}
      {schedulingServiceEnabled() && (
        <Route
          path="/agent/availability"
          element={
            <RequireRole role="agent">
              <AgentAvailabilityPage />
            </RequireRole>
          }
        />
      )}
      <Route
        path="/agent/calendar"
        element={
          <RequireRole role="agent">
            <AgentCalendarPage />
          </RequireRole>
        }
      />
      <Route
        path="/agent/copy"
        element={
          <RequireRole role="agent">
            <AgentCopySchedulePage />
          </RequireRole>
        }
      />
      <Route
        path="/agent/schedule-template"
        element={
          <RequireRole role="agent">
            <AgentScheduleTemplatePage />
          </RequireRole>
        }
      />
      <Route
        path="/agent/request-leave"
        element={
          <RequireRole role="agent">
            <AgentRequestLeavePage />
          </RequireRole>
        }
      />
      <Route
        path="/agent/request-history"
        element={
          <RequireRole role="agent">
            <AgentRequestHistoryPage />
          </RequireRole>
        }
      />
      <Route
        path="/supervisor"
        element={
          <RequireRole role="supervisor">
            <SupervisorDashboardPage />
          </RequireRole>
        }
      />
      <Route
        path="/supervisor/approval-inbox"
        element={
          <RequireRole role="supervisor">
            <SupervisorApprovalInboxPage />
          </RequireRole>
        }
      />
      {schedulingServiceEnabled() && (
        <Route
          path="/supervisor/open-shifts"
          element={
            <RequireRole role="supervisor">
              <SupervisorOpenShiftsPage />
            </RequireRole>
          }
        />
      )}
      <Route
        path="/supervisor/planning-board"
        element={
          <RequireRole role="supervisor">
            <SupervisorPlanningBoardPage />
          </RequireRole>
        }
      />
      <Route
        path="/supervisor/daily-assignments"
        element={
          <RequireRole role="supervisor">
            <SupervisorDailyAssignmentsPage />
          </RequireRole>
        }
      />
      <Route
        path="/supervisor/personnel"
        element={
          <RequireRole role="supervisor">
            <SupervisorPersonnelDirectoryPage />
          </RequireRole>
        }
      />
      <Route
        path="/supervisor/personnel/:id"
        element={
          <RequireRole role="supervisor">
            <SupervisorPersonnelProfilePage />
          </RequireRole>
        }
      />
      <Route path="/supervisor/reports" element={<Navigate to="/supervisor/reports/daily-roster" replace />} />
      {REPORT_CATALOG.map((report) => (
        <Route
          key={`supervisor-${report.id}`}
          path={`/supervisor/reports/${report.id}`}
          element={
            <RequireRole role="supervisor">
              <SupervisorReportPage report={report.id} />
            </RequireRole>
          }
        />
      ))}
      <Route
        path="/supervisor/staffing-targets"
        element={
          <RequireRole role="supervisor">
            <SupervisorStaffingTargetsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireRole role="administrator">
            <AdminDashboardPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/accounts"
        element={
          <RequireRole role="administrator">
            <AdminAccountsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/teams"
        element={
          <RequireRole role="administrator">
            <AdminTeamsPage />
          </RequireRole>
        }
      />
      {REPORT_CATALOG.map((report) => (
        <Route
          key={`admin-${report.id}`}
          path={`/admin/reports/${report.id}`}
          element={
            <RequireRole role="administrator">
              <AdminReportPage report={report.id} />
            </RequireRole>
          }
        />
      ))}
      <Route
        path="/admin/audit-log"
        element={
          <RequireRole role="administrator">
            <AdminAuditLogPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/shift-templates"
        element={
          <RequireRole role="administrator">
            <AdminShiftTemplatesPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/codes"
        element={
          <RequireRole role="administrator">
            <AdminCodesPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/leave-types"
        element={
          <RequireRole role="administrator">
            <AdminLeaveTypesPage />
          </RequireRole>
        }
      />
      <Route
        path="/leadership"
        element={
          <RequireRole role="leadership">
            <LeadershipDashboardPage />
          </RequireRole>
        }
      />
    </Routes>
  )
}
